import { css } from '@emotion/core';
import dayjs from 'dayjs';
import { withPrefix } from 'gatsby';
import { Box, Card, Heading } from 'lucid-components-react';
import React from 'react';

import Link from 'components/link/link';
import { mqMin } from 'theme/base';
import { Text } from 'theme/typography';
import { LucidTheme } from 'types/types';

enum tileSize {
  large = 'large',
  medium = 'medium'
}

interface TilesGridProps {
  tiles: any[];
  pageType: string;
  page?: number;
  pageSize: number;
  currentPage: number;
  isUpcomingWebinars?: boolean;
}

interface TileNode {
  entityUrl?: {
    path: string;
  }
  fieldDate?: {
    value: string;
  }
  fieldDescription?: {
    summary: string;
  }
  fieldImageSrc: string;
  fieldSticky?: boolean;
  fieldTitle?: string;
  fieldWebinarDescription?: {
    summary: string;
  };
  fieldWebinarDate?: {
    value: string;
  };
  nid: string;
  size?: tileSize;
  title?: string;
}

interface TileProps {
  tile: TileNode;
  pageType: string;
}

const tileHalfStyles = () =>
  css({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    height: '100%',
    width: '100%',
    [mqMin.tabletSmall]: {
      width: 'calc(50% - 20px)'
    }
  });

type TileGroupMap = {
  largeTiles: TileNode[];
  tiles: TileNode[];
  isRight: boolean;
};

const Tile = ({ tile, pageType }: TileProps) => {
  const isMedium = tile.size === tileSize.medium;
  const isLarge = tile.fieldSticky || tile.size === tileSize.large;
  const description = tile.fieldWebinarDescription || tile.fieldDescription;
  const fieldDate = tile.fieldWebinarDate || tile.fieldDate;
  const title = tile.fieldTitle || tile.title;

  return (
    <Card
      className={`${pageType}-tile-card`}
      css={(theme: LucidTheme) => [
        {
          label: `${pageType}-tile-card`,
          background: theme.colors.white,
          height: '100%',
          maxHeight: '580px',
          marginBottom: theme.space[5],
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
          transition: 'all 0.25s cubic-bezier(0.17, 0.67, 0.52, 0.97)',
          border: `1px solid ${theme.colors.coolGrey[10]}`,
          [mqMin.tabletSmall]: {
            maxHeight: '420px'
          },
          '&:hover': {
            transform: 'translateY(-5px)',
            [`& .${pageType}__tile-content`]: {
              '&::after': {
                transform: 'scaleX(1)',
                transformOrigin: 'bottom left'
              }
            },
            [`& .${pageType}__tile-title`]: {
              color: theme.colors.orange[30]
            }
          }
        },
        isLarge && {
          [mqMin.tabletSmall]: {
            maxHeight: '580px'
          }
        },
        isMedium && {
          [mqMin.tabletSmall]: {
            maxHeight: '270px',
            marginBottom: theme.space[6]
          }
        }
      ]}
      variant="interactive"
    >
      <Link
        newTab
        css={[
          { height: '100%', overflow: 'hidden' },
          isMedium && {
            [mqMin.tabletSmall]: {
              display: 'flex'
            }
          }
        ]}
        to={withPrefix(tile.entityUrl!.path!)}
        underline="none"
      >
        <Card.Image
          className={`${pageType}-imageContainer`}
          css={[
            {
              label: 'card-imageContainer',
              position: 'relative',
              overflow: 'hidden',
              maxHeight: '285px',
              '&::before': {
                display: 'block',
                content: '""',
                paddingTop: '56.25%'
              }
            },
            isLarge && {
              [mqMin.tabletSmall]: {
                height: '256px'
              },
              [mqMin.computer]: {
                maxHeight: '290px',
                height: 'unset'
              }
            },
            isMedium && {
              [mqMin.tabletSmall]: {
                '&::before': {
                  paddingTop: '100%'
                }
              }
            }
          ]}
        >
          <Box
            css={[
              {
                label: 'cardBGImage',
                backgroundImage: `url('${tile.fieldImageSrc ||
                  'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/examples/org-chart/org-chart-template.jpg'}')`,
                height: '100%',
                width: '100%',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundClip: 'content-box',
                position: 'absolute',
                top: 0,
                bottom: 0
              }
            ]}
          />
        </Card.Image>
        <Card.Content
          className={`${pageType}__tile-content`}
          css={(theme: LucidTheme) => [
            {
              background: theme.colors.white,
              color: theme.colors.coolGrey[100],
              padding: theme.space[5],
              position: 'relative',
              height: '100%',
              maxHeight: '375px',
              verticalAlign: 'top',
              [mqMin.mobile]: {
                maxHeight: '285px'
              },
              [mqMin.tabletSmall]: {
                maxHeight: '322px'
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                boxShadow: `0 -30px 40px 5px ${theme.colors.white} inset`,
                borderTop: `6px solid ${theme.colors.coolGrey[20]}`
              },
              '&::after': {
                content: '""',
                width: '100%',
                height: '6px',
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: theme.colors.orange[30],
                transformOrigin: 'bottom right',
                transform: 'scaleX(0)',
                transition: 'transform 0.25s ease-out',
                zIndex: theme.zIndices[1]
              }
            },
            isLarge && {
              [mqMin.tabletSmall]: {
                width: '100%',
                height: '50%',
                display: 'block'
              },
              [mqMin.computer]: {
                maxHeight: '290px',
                paddingTop: '38px'
              }
            },
            isMedium && {
              [mqMin.tabletSmall]: {
                maxHeight: '270px',
                maxWidth: '50%',
              }
            }
          ]}
        >
          {fieldDate && (
            <Text
              css={(theme: LucidTheme) => [
                {
                  marginTop: theme.space[2],
                  marginBottom: theme.space[3]
                },
                isMedium && {
                  [mqMin.tabletSmall]: {
                    marginTop: theme.space[0]
                  }
                }
              ]}
              textStyles="introXS300"
            >
              {dayjs(fieldDate!.value)
                .subtract(6, 'hour')
                .format('MMMM DD, YYYY')}
            </Text>
          )}
          <Heading
            as="h3"
            className={`${pageType}__tile-title`}
            css={(theme: LucidTheme) => [
              {
                marginTop: theme.space[2],
                marginBottom: theme.space[2]
              },
              isMedium && {
                [mqMin.tabletSmall]: {
                  marginTop: theme.space[0],
                  marginBottom: theme.space[0],
                  fontWeight: theme.fontWeights.bold,
                  fontSize: theme.fontSizes[5],
                  lineHeight: 0.96,
                  color: theme.colors.coolGrey[100]
                }
              }
            ]}
            variant="subheadM700"
          >
            {title}
          </Heading>
          <Text
            css={[
              isLarge && {
                [mqMin.tabletLandscape]: {
                  minHeight: '514px'
                }
              }
            ]}
            textStyles="bodyS400"
          >
            {description!.summary}
          </Text>
        </Card.Content>
      </Link>
    </Card>
  );
};

const TilesGrid = ({
  tiles,
  pageType,
  page = 1,
  pageSize,
  currentPage
}: TilesGridProps) => {
  const tileGroups = (currentTiles: TileNode[]) =>
    currentTiles
      .map((_, i) =>
        i % pageSize === 0 ? currentTiles.slice(i, i + pageSize) : null
      )
      .filter(Boolean);

  const currentTileGroup = tileGroups(tiles)
    .slice(0, currentPage - page + 1)
    .reduce<TileGroupMap[]>((previous, current, index) => {
      const largeTile = current!.find(currentTile => {
        if (currentTile.fieldSticky) {
          return currentTile;
        }
        return null;
      });

      const currentLargeTiles = largeTile
        ? largeTile
        : {
          ...current![0],
          fieldSticky: true,
          size: tileSize.large
        };

      const otherTiles = current!.filter(
        currentTile => currentTile.nid !== currentLargeTiles.nid
      );

      const otherTilesSizes = otherTiles.map((currentTile, i) => {
        const size =
          i === 0
            ? i === otherTiles.length - 1
              ? tileSize.large
              : tileSize.medium
            : tileSize.medium;

        return {
          ...currentTile,
          size
        };
      });

      const isRight =
        index % 2 === 0 ? (current!.length === 1 ? false : true) : false;

      return [
        ...previous,
        {
          largeTiles: [currentLargeTiles],
          tiles: [...otherTilesSizes],
          isRight: isRight
        }
      ];
    }, []);

  return (
    <>
      {currentTileGroup.map(({ largeTiles, tiles, isRight }, i: number) => (
        <Box
          key={`${pageType}-tile-group-${i}`}
          as="article"
          css={(theme: LucidTheme) => ({
            label: `${pageType}-tile-group-container`,
            [mqMin.tabletSmall]: {
              display: 'flex',
              flexFlow: 'row nowrap',
              justifyContent: 'space-between',
              paddingTop: theme.space[6]
            },
            [`& .${pageType}__half`]: {
              '&:nth-of-type(1)': {
                order: isRight ? 1 : 'unset'
              }
            }
          })}
        >
          <Box
            key={`${pageType}-largeTile-${i}`}
            className={`${pageType}__half`}
            css={tileHalfStyles()}
          >
            {largeTiles?.map((tile: TileNode, index) => (
              <Tile
                key={`${pageType}-largeTile-${index}`}
                pageType={pageType}
                tile={tile}
              />
            ))}
          </Box>
          <Box
            key={`${pageType}-tiles-${i}`}
            className={`${pageType}__half`}
            css={tileHalfStyles()}
          >
            {tiles?.map((tile: TileNode, index) => (
              <Tile
                key={`${pageType}-tile-${index}`}
                pageType={pageType}
                tile={tile}
              />
            ))}
          </Box>
        </Box>
      ))}
    </>
  );
};

export default TilesGrid;
